import type { AssortmentDemand } from '../../gql';

import { randNumber, randBoolean, randUuid, randWord } from '@ngneat/falso';

import {
    mockDeliveryPeriod,
    mockStandardText,
    mockSticker,
    mockPackingInstruction,
    mockBusinessTradingPartner,
    mockLockedField,
} from './mockDemandFragments';

/**
 * Will return a mocked AssortmentDemand object, useful for tests etc.
 * By default, creates a valid assortment based on determineFaultyAssortment validation rules:
 * - styleEanNumber is always null (to avoid BE calls that would fail with mock data)
 * - Since styleEanNumber is null, assortmentName and assortmentHasLines are always set
 * - All isDeleted flags are false
 */
const mockAssortmentDemand = (
    overwrite?: Partial<AssortmentDemand>
): AssortmentDemand => {
    const baseAssortmentDemand: AssortmentDemand = {
        assortmentHasLines: true,
        assortmentName: `Assortment ${randWord({ capitalize: true })}`,
        assortmentNumber: randNumber({ min: 1000000, max: 9999999 }),
        assortmentSize: randNumber({ min: 1, max: 20 }),
        businessTradingPartner: randBoolean()
            ? mockBusinessTradingPartner()
            : null,
        comment: randBoolean() ? mockStandardText() : null,
        commentNumber: randBoolean()
            ? randNumber({ min: 1000, max: 9999 })
            : null,
        customerGroupMappingNumber: randBoolean()
            ? randNumber({ min: 100000, max: 999999 }).toString()
            : null,
        deliveryPeriod: mockDeliveryPeriod(),
        demandNumber: randUuid(),
        isFreeForAll: randBoolean(),
        keyCustomer: randBoolean(),
        lockedFields: randBoolean()
            ? [mockLockedField(), mockLockedField()]
            : [],
        ownClearance: randBoolean(),
        packingInstruction: randBoolean() ? mockPackingInstruction() : null,
        packingText: randBoolean() ? mockStandardText() : null,
        packingTextNumber: randBoolean()
            ? randNumber({ min: 1000, max: 9999 })
            : null,
        polyBagged: randBoolean(),
        quantity: randNumber({ min: 100, max: 10000 }),
        ringfencing: {
            isRingfencing: false,
            ringfencingType: null,
        },
        sticker: mockSticker(),
        styleEanNumber: null,
    };

    return { ...baseAssortmentDemand, ...overwrite };
};

export { mockAssortmentDemand };
